import * as React from "react";
import {useEffect, useState} from "react";
import {LoginContentAdminApi} from "../../../api/LoginContentAdminApi";
import {DialogComponent} from "../../common/Dialog.component";
import {IToastNotification} from "../../../utils/planUserRemoval";
import {AdminLoginCarouselCardForm} from "./AdminLoginCarouselCardForm";
import {AdminLoginCarouselCardsList} from "./AdminLoginCarouselCardsList";
import {AdminRegion, CarouselCard} from "../../../model/RegionLoginContent.model";

export const ADMIN_LOGIN_CAROUSEL_DESCRIPTION = "Rotating cards shown on the login marketing panel.";
export const ADMIN_LOGIN_CAROUSEL_ADD_BTN = "Add Card";
export const ADMIN_LOGIN_CAROUSEL_CARD_CREATED = "Card created.";
export const ADMIN_LOGIN_CAROUSEL_CARD_SAVED = "Card saved.";
export const ADMIN_LOGIN_CAROUSEL_CARD_DELETED = "Card deleted.";
export const ADMIN_LOGIN_CAROUSEL_ORDER_SAVED = "Card order saved.";
export const ADMIN_LOGIN_CAROUSEL_SAVE_ERROR = "Failed to save carousel cards";
export const ADMIN_LOGIN_CAROUSEL_DELETE_CONFIRM =
    "Are you sure you want to delete this carousel card? This cannot be undone.";

interface AdminLoginCarouselCardsContentProps {
    activeRegion: AdminRegion;
    setToast: (toast: IToastNotification) => void;
    onCarouselSaved: (regionId: number) => void;
}

export const AdminLoginCarouselCardsContent: React.FunctionComponent<AdminLoginCarouselCardsContentProps> = (props) => {
    const [cards, setCards] = useState<CarouselCard[]>(props.activeRegion.carouselCards ?? []);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

    useEffect(() => {
        setCards(props.activeRegion.carouselCards ?? []);
        setEditingIndex(null);
        setShowAddForm(false);
        setDeleteIndex(null);
    }, [props.activeRegion.id, props.activeRegion.carouselCards]);

    // Each action persists the whole card list immediately
    // and refreshes the region, which flips its status to Draft.
    const persist = (nextCards: CarouselCard[], successMessage: string) => {
        setCards(nextCards);
        LoginContentAdminApi.saveCarouselCards(props.activeRegion.id, nextCards)
            .then(() => {
                props.setToast({message: successMessage, type: "success"});
                props.onCarouselSaved(props.activeRegion.id);
            })
            .catch(() => {
                props.setToast({message: ADMIN_LOGIN_CAROUSEL_SAVE_ERROR, type: "error"});
            });
    };

    const closeForm = () => {
        setShowAddForm(false);
        setEditingIndex(null);
    };

    const createCard = (card: CarouselCard) => {
        persist([...cards, card], ADMIN_LOGIN_CAROUSEL_CARD_CREATED);
        closeForm();
    };

    const saveCard = (card: CarouselCard) => {
        if (editingIndex === null) return;
        persist(cards.map((existing, i) => i === editingIndex ? card : existing), ADMIN_LOGIN_CAROUSEL_CARD_SAVED);
        closeForm();
    };

    const toggleActive = (index: number, active: boolean) => {
        persist(cards.map((card, i) => i === index ? {...card, active} : card),
            active ? "Card shown." : "Card hidden.");
    };

    const confirmDelete = () => {
        if (deleteIndex === null) return;
        persist(cards.filter((_, i) => i !== deleteIndex), ADMIN_LOGIN_CAROUSEL_CARD_DELETED);
        if (editingIndex === deleteIndex) closeForm();
        setDeleteIndex(null);
    };

    return (
        <div className="admin-login-content__panel">
            <div className="admin-login-content__carousel-header">
                <p className="admin-login-content__panel-description">{ADMIN_LOGIN_CAROUSEL_DESCRIPTION}</p>
                {!showAddForm && (
                    <button
                        type="button"
                        className="admin-login-content__btn admin-login-content__btn--primary admin-login-content__btn--sm"
                        data-testid="admin-login-carousel__add-btn"
                        onClick={() => {
                            setEditingIndex(null);
                            setShowAddForm(true);
                        }}>
                        <i className="fa-solid fa-plus" /> {ADMIN_LOGIN_CAROUSEL_ADD_BTN}
                    </button>
                )}
            </div>

            {showAddForm && (
                <AdminLoginCarouselCardForm
                    key="add"
                    isEdit={false}
                    setToast={props.setToast}
                    onSubmit={createCard}
                    onCancel={closeForm}/>
            )}

            <AdminLoginCarouselCardsList
                cards={cards}
                editingIndex={editingIndex}
                editForm={editingIndex !== null && (
                    <AdminLoginCarouselCardForm
                        key={`edit-${editingIndex}`}
                        isEdit={true}
                        initialCard={cards[editingIndex]}
                        setToast={props.setToast}
                        onSubmit={saveCard}
                        onCancel={closeForm}/>
                )}
                onReorder={(reordered) => persist(reordered, ADMIN_LOGIN_CAROUSEL_ORDER_SAVED)}
                onToggleActive={toggleActive}
                onEdit={(index) => {
                    setShowAddForm(false);
                    setEditingIndex(index);
                }}
                onDelete={setDeleteIndex}/>

            <DialogComponent
                open={deleteIndex !== null}
                title="Delete Card"
                description={ADMIN_LOGIN_CAROUSEL_DELETE_CONFIRM}
                yesText="Delete"
                noText="Cancel"
                onYes={confirmDelete}
                onNo={() => setDeleteIndex(null)}/>
        </div>
    );
};
